import type { TFunction } from 'i18next';

/**
 * Maps each Add/Edit Device form field id (as returned by the
 * `getUIMetadata` API) to the i18n keys for its rule messages.
 *
 * Why: the API returns hard-coded English `rules.required.message` /
 * `rules.pattern.message` strings, which react-hook-form surfaces as
 * `error.message`. We override those messages with localized values by
 * the well-known field id, so the translation lives next to the screen
 * that owns the field — not inside the generic Input/Element components.
 *
 * To localize messages for a new field, add another entry keyed by its
 * metadata `id`. To localize a different metadata-driven flow, copy this
 * helper next to that flow with its own field map.
 */
const FIELD_MESSAGE_KEYS: Record<string, { required?: string; pattern?: string }> = {
    deviceId: {
        required: 'ADD_DEVICE_FORM:GUID_IS_REQUIRED',
        pattern: 'ADD_DEVICE_FORM:INVALID_GUID',
    },
};

/**
 * Returns a copy of the metadata-driven `elements` array with each
 * field's `rules.required.message` / `rules.pattern.message` replaced by
 * the localized string for that field id (when a mapping exists).
 * Fields without a mapping are returned unchanged.
 */
export const localizeAddDeviceElements = <T extends { id: string; rules?: any }>(
    elements: T[] | undefined | null,
    t: TFunction
): T[] => {
    if (!elements?.length) return elements ?? [];

    return elements.map((field) => {
        const overrides = FIELD_MESSAGE_KEYS[field.id];
        if (!overrides || !field.rules) return field;

        const nextRules = { ...field.rules };
        if (overrides.required && nextRules.required) {
            nextRules.required = { ...nextRules.required, message: t(overrides.required) };
        }
        if (overrides.pattern && nextRules.pattern) {
            nextRules.pattern = { ...nextRules.pattern, message: t(overrides.pattern) };
        }
        return { ...field, rules: nextRules };
    });
};
